import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { fetchEmployee, fetchSkillGap, fetchSkillRadar, fetchCareerPaths, fetchMentorMatches, fetchNineBoxEmployee } from "../lib/api";
import { Loader2, TrendingUp, Users2, Target, BookOpen, Star, Award, Grid3x3, AlertTriangle, Info } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";

const potentialColor = (p) => ({ dusuk: "#94a3b8", orta: "#0891b2", yuksek: "#059669" }[(p || "").toLowerCase()] || "#64748b");

export default function EmployeeDetailModal({ employeeId, open, onOpenChange }) {
  const [emp, setEmp] = useState(null);
  const [gap, setGap] = useState(null);
  const [radar, setRadar] = useState(null);
  const [paths, setPaths] = useState(null);
  const [mentors, setMentors] = useState(null);
  const [nineBox, setNineBox] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("nine-box");

  useEffect(() => {
    if (!employeeId || !open) return;
    setLoading(true);
    setTab("nine-box");
    Promise.all([
      fetchEmployee(employeeId),
      fetchSkillGap(employeeId).catch(() => null),
      fetchSkillRadar(employeeId).catch(() => null),
      fetchCareerPaths(employeeId).catch(() => ({ paths: [] })),
      fetchMentorMatches(employeeId).catch(() => ({ matches: [] })),
      fetchNineBoxEmployee(employeeId).catch(() => null),
    ])
      .then(([e, g, r, p, m, nb]) => { setEmp(e); setGap(g); setRadar(r); setPaths(p); setMentors(m); setNineBox(nb); })
      .finally(() => setLoading(false));
  }, [employeeId, open]);

  const radarData = (radar?.axes || []).map((a) => ({
    axis: a.skill_name || a.cluster_name,
    current: a.current,
    target: a.target,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="employee-detail-modal">
        <DialogHeader>
          <DialogTitle>Çalışan Detayı</DialogTitle>
        </DialogHeader>

        {loading || !emp ? (
          <div className="p-10 flex items-center justify-center gap-3 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" /> Detaylar yükleniyor...
          </div>
        ) : (
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
              <img src={emp.avatar} alt={emp.name} className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-sm" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
              <div className="flex-1 min-w-0">
                <div className="text-xl font-semibold text-slate-900">{emp.name}</div>
                <div className="text-sm text-slate-600">{emp.title} · {emp.department}</div>
                <div className="text-xs text-slate-500 mt-1">{emp.location} · {emp.branch_name || "-"} · Kıdem: {emp.tenure_years?.toFixed(1) || "-"} yıl</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">Perf: <b>{emp.performance_rating?.toFixed(1) || "–"}</b> {emp.performance_trend}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full text-white font-medium" style={{ background: potentialColor(emp.potential) }}>Potansiyel: {emp.potential || "–"}</span>
                  {emp.is_talent && <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium flex items-center gap-1"><Star className="w-2.5 h-2.5" /> HiPo</span>}
                  {emp.is_manager && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-medium">Yönetici</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">Band: {emp.salary_band || "-"}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">Bağlılık: {emp.engagement_score || "-"}</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
              {[
                { id: "nine-box", label: "9 Box Konumu", icon: <Grid3x3 className="w-3.5 h-3.5" /> },
                { id: "profile", label: "Profil", icon: <Users2 className="w-3.5 h-3.5" /> },
                { id: "skills", label: "Yetkinlikler", icon: <TrendingUp className="w-3.5 h-3.5" /> },
                { id: "career", label: "Kariyer Yolları", icon: <Target className="w-3.5 h-3.5" /> },
                { id: "mentors", label: "Mentor Eşleşmeleri", icon: <BookOpen className="w-3.5 h-3.5" /> },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`employee-detail-tab-${t.id}`}
                  className={`px-3 py-2 text-sm border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                    tab === t.id ? "border-indigo-600 text-indigo-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {/* 9 Box Konumu tab */}
            {tab === "nine-box" && nineBox && (
              <div className="space-y-3" data-testid="employee-detail-ninebox">
                <div className="p-3 rounded-lg border-2" style={{ borderColor: nineBox.cell.color + "40", background: nineBox.cell.color + "08" }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Grid3x3 className="w-5 h-5" style={{ color: nineBox.cell.color }} />
                    <span className="text-lg font-semibold" style={{ color: nineBox.cell.color }}>
                      {nineBox.cell.label}
                    </span>
                    <span className="text-xs text-slate-500">({nineBox.cell.x},{nineBox.cell.y})</span>
                    {nineBox.calibrated && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">Manuel Kalibre</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-700 mt-2">{nineBox.reasoning?.summary}</div>
                  {nineBox.calibrated && nineBox.calibration_rationale && (
                    <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-indigo-700 italic">
                      <b>Kalibrasyon gerekçesi:</b> {nineBox.calibration_rationale}
                      {nineBox.auto_cell && nineBox.auto_cell.x !== nineBox.cell.x && (
                        <div className="mt-1 text-slate-500 not-italic">
                          Otomatik konum: <b>{nineBox.auto_cell.label}</b> ({nineBox.auto_cell.x},{nineBox.auto_cell.y})
                        </div>
                      )}
                    </div>
                  )}
                  {nineBox.reasoning?.new_hire_warning && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>Yeni işe alım / rol değişikliği (kıdem &lt; 6 ay). İlk 6 ayda 9 Box konumlandırması genelde geçici sayılır.</span>
                    </div>
                  )}
                </div>

                {/* Performans ekseni */}
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Performans Ekseni (Geçmiş)</div>
                    <span className="text-xs text-slate-600">— {nineBox.reasoning?.performance?.axis}</span>
                  </div>
                  <div className="space-y-1.5">
                    {(nineBox.reasoning?.performance?.factors || []).map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <div className="flex-1 text-slate-700">{f.label}</div>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-0.5">
                            {[1,2,3,4,5].map((n) => (
                              <span key={n} className={`w-1.5 h-4 rounded-sm ${n <= f.score ? "bg-emerald-500" : "bg-slate-200"}`} />
                            ))}
                          </div>
                          <div className="text-xs font-mono text-slate-800 w-20 text-right">{f.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Potansiyel ekseni (3 bileşen) */}
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Potansiyel Ekseni (Gelecek)</div>
                    <span className="text-xs text-slate-600">— {nineBox.reasoning?.potential?.axis}</span>
                    <span title="Potansiyel 3 bileşenden hesaplanır: öğrenme çevikliği, istek (aspiration), bağlılık & mobilite." className="text-slate-400"><Info className="w-3 h-3" /></span>
                  </div>
                  <div className="space-y-2">
                    {(nineBox.reasoning?.potential?.factors || []).map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <div className="flex-1">
                          <div className="text-slate-700">{f.label}</div>
                          <div className="text-[11px] text-slate-500 italic">{f.note}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-0.5">
                            {[1,2,3,4,5].map((n) => (
                              <span key={n} className={`w-1.5 h-4 rounded-sm ${n <= f.score ? "bg-indigo-500" : "bg-slate-200"}`} />
                            ))}
                          </div>
                          <div className="text-xs font-mono text-slate-800 w-10 text-right">{f.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Önerilen aksiyon */}
                {nineBox.reasoning?.recommendation && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <div className="text-[10px] uppercase tracking-wide text-amber-800 font-semibold mb-1">Önerilen Aksiyon</div>
                    <div className="text-sm text-amber-900">{nineBox.reasoning.recommendation}</div>
                  </div>
                )}
              </div>
            )}

            {/* Profile tab */}
            {tab === "profile" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">İşe Giriş</div>
                  <div className="text-slate-800">{emp.hire_date || "-"}</div>
                </div>
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Eğitim</div>
                  <div className="text-slate-800">{emp.education || "-"}{emp.university ? ` · ${emp.university}` : ""}</div>
                </div>
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Yaş / Cinsiyet</div>
                  <div className="text-slate-800">{emp.age || "-"} · {emp.gender || "-"}</div>
                </div>
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Yıllık Satış Gerçekleşme</div>
                  <div className="text-slate-800">{emp.avg_sales_achievement_pct ? `%${emp.avg_sales_achievement_pct}` : "—"}</div>
                </div>
                {emp.engagement_breakdown && (
                  <div className="p-3 rounded-lg bg-white border border-slate-100 md:col-span-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Bağlılık Kırılımı</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div>İş Tatmini: <b>{emp.engagement_breakdown.satisfaction}/5</b></div>
                      <div>Yönetim: <b>{emp.engagement_breakdown.management}/5</b></div>
                      <div>Kariyer: <b>{emp.engagement_breakdown.career}/5</b></div>
                      <div>NPS: <b>{emp.engagement_breakdown.nps}/10</b></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Skills tab */}
            {tab === "skills" && (
              <div className="space-y-3">
                {radarData.length > 0 ? (
                  <div className="p-3 rounded-lg bg-white border border-slate-100">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Yetkinlik Radarı</div>
                    <ResponsiveContainer width="100%" height={280}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#e2e8f0" />
                        <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11 }} />
                        <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                        <Radar name="Mevcut" dataKey="current" stroke="#0891b2" fill="#0891b2" fillOpacity={0.3} />
                        <Radar name="Hedef" dataKey="target" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500 italic p-3">Yetkinlik verisi yok.</div>
                )}
                {gap && (
                  <div className="p-3 rounded-lg bg-emerald-50/50 border border-emerald-100">
                    <div className="text-xs text-emerald-800">
                      <b>Hazır Olma Skoru: %{gap.readiness_score}</b> — {gap.gaps?.length || 0} gap tespit edildi.
                    </div>
                    {gap.gaps && gap.gaps.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {gap.gaps.slice(0, 8).map((g, i) => (
                          <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-800">
                            {g.skill_name} <span className="text-emerald-500">({g.current}→{g.target})</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Career paths tab */}
            {tab === "career" && (
              <div className="space-y-2">
                {(paths?.paths || []).length === 0 ? (
                  <div className="text-sm text-slate-500 italic p-3">Uygun kariyer yolu bulunamadı.</div>
                ) : (
                  paths.paths.slice(0, 6).map((p, i) => (
                    <div key={i} className="p-3 rounded-lg bg-white border border-slate-100 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{p.role_title}</div>
                        <div className="text-xs text-slate-500">Band {p.career_band} · {p.department} · {p.time_to_ready_months || "—"} ay</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {p.is_promotion && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Terfi</span>}
                        <div className="text-right">
                          <div className="text-lg font-bold text-indigo-600">%{p.match_score}</div>
                          <div className="text-[9px] text-slate-500 uppercase">uygunluk</div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Mentors tab */}
            {tab === "mentors" && (
              <div className="space-y-2">
                {(mentors?.matches || []).length === 0 ? (
                  <div className="text-sm text-slate-500 italic p-3">Uygun mentor bulunamadı.</div>
                ) : (
                  mentors.matches.slice(0, 5).map((m) => (
                    <div key={m.mentor_id} className="p-3 rounded-lg bg-white border border-slate-100 flex items-center gap-3">
                      <img src={m.avatar} alt={m.name} className="w-10 h-10 rounded-full object-cover" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{m.name}</div>
                        <div className="text-xs text-slate-500 truncate">{m.title}</div>
                        {m.expertise_skills?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {m.expertise_skills.slice(0, 3).map((s, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-700">{s}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-indigo-600">%{m.overall_score}</div>
                        <div className="text-[9px] text-slate-500 uppercase flex items-center gap-0.5 justify-end"><Award className="w-2.5 h-2.5" /> eşleşme</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
